import type { FilterName, SortName, WishlistGame, WishlistOffer } from "./types";

export type PlatformFilter = "all" | "pc" | "ps";
const PC_STORES = new Set(["steam_kz", "steambuy", "gabestore", "payberry", "igrovoy", "ggsel", "plati"]);

export function gamePlatforms(game: WishlistGame): PlatformFilter[] {
  const platforms = new Set<PlatformFilter>();
  for (const offer of game.offers) {
    if (PC_STORES.has(offer.store) || offer.platform === "PC") platforms.add("pc");
    if (offer.store === "ps_tr" || offer.store === "ps_in" || ["PS4", "PS5"].includes(offer.platform ?? "")) platforms.add("ps");
  }
  return [...platforms].sort();
}

export function rankedOffers(game: WishlistGame): WishlistOffer[] {
  return game.offers.slice().sort((a, b) =>
    Number(b.id === game.best_offer_id) - Number(a.id === game.best_offer_id)
    || (a.effective_price_rub ?? Infinity) - (b.effective_price_rub ?? Infinity)
    || a.label.localeCompare(b.label, "ru") || a.id - b.id);
}

export function bestDiscount(game: WishlistGame): number | null {
  const best = game.offers.find(offer => offer.id === game.best_offer_id);
  return best?.effective_price_kind === "ps_plus" ? best.ps_plus_discount_percent : best?.discount_percent ?? null;
}

export function fitOfferCount(widths: number[], moreWidth: number, width: number, gap = 5): number {
  const fits = (items: number[]) => {
    let rows = 1, used = 0;
    for (const item of items) {
      if (used > 0 && used + gap + item > width) { rows++; used = 0; }
      used += (used > 0 ? gap : 0) + Math.min(item, width);
    }
    return rows <= 2;
  };
  for (let count = widths.length; count > 1; count--) {
    if (fits([...widths.slice(0, count), ...(count < widths.length ? [moreWidth] : [])])) return count;
  }
  return Math.min(1, widths.length);
}

export type GameLocalization = "text_audio" | "text" | "none" | "unknown";

export function gameLocalization(game: WishlistGame): GameLocalization {
  const statuses = game.offers.map((offer) => offer.localization_status);
  if (statuses.includes("text_audio")) return "text_audio";
  if (statuses.includes("text")) return "text";
  if (statuses.length > 0 && statuses.every((status) => status === "none")) return "none";
  return "unknown";
}

export function hasRussianLocalization(game: WishlistGame): boolean {
  return ["text", "text_audio"].includes(gameLocalization(game));
}

export function visibleGames(
  games: WishlistGame[],
  query: string,
  filter: FilterName,
  sort: SortName,
  platform: PlatformFilter = "all",
): WishlistGame[] {
  const words = query.trim().toLocaleLowerCase("ru").split(/\s+/).filter(Boolean);
  const filtered = games.filter((game) => {
    if (platform !== "all" && !gamePlatforms(game).includes(platform)) return false;
    const title = game.title.toLocaleLowerCase("ru");
    if (!words.every((word) => title.includes(word))) return false;
    if (filter === "sale") return game.has_discount;
    if (filter === "missing") return !game.has_price;
    if (filter === "upcoming") return game.status !== "released";
    if (filter === "ps_plus") return game.has_ps_plus;
    if (filter === "russian") return hasRussianLocalization(game);
    return true;
  });
  if (sort === "default") {
    if (filter !== "upcoming") return filtered;
    return filtered.slice().sort((left, right) => {
      const leftRelease = left.release_date ? Date.parse(left.release_date) : Number.POSITIVE_INFINITY;
      const rightRelease = right.release_date ? Date.parse(right.release_date) : Number.POSITIVE_INFINITY;
      return leftRelease - rightRelease || left.title.localeCompare(right.title, "ru");
    });
  }
  return filtered.slice().sort((left, right) => {
    if (sort === "title") return left.title.localeCompare(right.title, "ru");
    if (sort === "discount") {
      return (right.max_discount_percent ?? -1) - (left.max_discount_percent ?? -1);
    }
    const leftPrice = left.best_price_rub ?? Number.POSITIVE_INFINITY;
    const rightPrice = right.best_price_rub ?? Number.POSITIVE_INFINITY;
    return leftPrice - rightPrice || left.title.localeCompare(right.title, "ru");
  });
}

export function filterCount(games: WishlistGame[], filter: FilterName, platform: PlatformFilter = "all"): number {
  return visibleGames(games, "", filter, "default", platform).length;
}

export function formatRub(value: number | null): string {
  if (value === null) return "Нет цены";
  return `${Math.round(value).toLocaleString("ru-RU")} ₽`;
}

export function formatNative(minor: number | null, currency: string | null): string {
  if (minor === null || !currency) return "Нет цены";
  try {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(minor / 100);
  } catch {
    return `${(minor / 100).toLocaleString("ru-RU")} ${currency}`;
  }
}

export function formatDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}
